import { ProfileDto } from "src/types/Profile";
import { UserDetails } from "src/types/userDetails";

export const mapUserDetailsToProfileDto = (
  user: UserDetails | null
): ProfileDto | null => {
  if (!user) return null;

  return {
  personalDetails: {
    firstName: user.personalDetails.firstName,
    lastName: user.personalDetails.lastName,
    dob: user?.personalDetails.dob,
    gender: user?.personalDetails.gender,
    maritalStatus: user?.personalDetails.maritalStatus,
    aadhaarCardNumber: user?.personalDetails.aadhaarCardNumber,
  },
  communicationDetails: {
    mobileNumber: user?.communicationDetails.mobileNumber,
    altMobileNumber: (user?.communicationDetails as any).altMobileNumber ?? user?.communicationDetails.alternateMobileNumber,
    email: user?.emailId,
    altEmailId: (user?.communicationDetails as any).altEmailId ?? user?.communicationDetails.alternateEmailId,
    addressLine1: user?.communicationDetails.addressLine1,
    addressLine2: user?.communicationDetails.addressLine2 ?? "",
    city: null,
    state: user?.communicationDetails.state,
    pinCode: user?.communicationDetails.pincode,
    currentAddress: user?.communicationDetails.currentAddress,
    permanentAddress: user?.communicationDetails.permanentAddress,
  },
  businessDetails: {
    educationalQualification: user?.businessDetails.educationalQualification,
    occupation: user?.businessDetails.occupation,
    business: user?.businessDetails.businessDetails,
  },
  familyDetails: {
    fatherName: user?.personalDetails.fatherName,
    motherName: user?.personalDetails.motherName,
  },
};
};

// Backend update payload shape matching UpdateProfileDto expectations
export interface UpdateProfilePayload {
  bio?: string | null;
  businessDetails?: string | null;
  occupation?: string | null;
  educationalQualification?: string | null;
  maritalStatus?: string | null;
  mobileNo?: string | null;
  altMobileNumber?: string | null;
  altEmailId?: string | null;
  villageCity?: string | null; // top-level village/city (communication)
  postOffice?: string | null;  // top-level post office (communication)
  currentAddress?: {
    addressLine1?: string | null;
    state?: string | null;
    pincode?: string | null;
    villageCity?: string | null;
    postOffice?: string | null;
  } | null;
  permanentAddress?: {
    addressLine2?: string | null;
    state?: string | null;
    pincode?: string | null;
    villageCity?: string | null;
    postOffice?: string | null;
  } | null;
  // NOTE: firstName / lastName are NOT in backend UpdateProfileDto now so cannot be updated
}

export const mapProfileToUpdatePayload = (profile: ProfileDto): UpdateProfilePayload => {
  const comm = (profile as any).communicationDetails || {};
  const current = (profile as any).currentAddress || {
    addressLine1: comm.addressLine1,
    addressLine2: comm.addressLine2,
    state: comm.state,
    pincode: comm.pinCode,
    villageCity: comm.city,
    postOffice: (profile as any).postOffice,
  };
  const permanent = (profile as any).permanentAddress || null;

  const topAddressLine1 = current?.addressLine1 ?? comm.addressLine1 ?? null;
  const topAddressLine2 = current?.addressLine2 ?? comm.addressLine2 ?? null;
  const topState = current?.state ?? comm.state ?? null;
  const topPincode = current?.pincode ?? comm.pinCode ?? null;
  const topVillageCity = current?.villageCity ?? comm.city ?? null;
  const topPostOffice = (profile as any).postOffice ?? current?.postOffice ?? null;

  return {
    bio: (profile as any).bio || null,
    businessDetails: profile.businessDetails?.business || null,
    occupation: profile.businessDetails?.occupation || null,
    educationalQualification: profile.businessDetails?.educationalQualification || null,
    maritalStatus: profile.personalDetails?.maritalStatus || null,
    mobileNo: comm.mobileNumber || null,
    altMobileNumber: comm.altMobileNumber || null,
    email: comm.email || null,
    altEmailId: comm.altEmailId || null,
    // top-level address fields expected by backend DTO
    addressLine1: topAddressLine1,
    addressLine2: topAddressLine2,
    state: topState,
    pincode: topPincode,
    villageCity: topVillageCity,
    postOffice: topPostOffice,
    currentAddress: {
      addressLine1: topAddressLine1,
      addressLine2: topAddressLine2,
      state: topState,
      pincode: topPincode,
      villageCity: topVillageCity,
      postOffice: topPostOffice,
    },
    permanentAddress: permanent
      ? {
          addressLine1: permanent.addressLine1 ?? null,
          addressLine2: permanent.addressLine2 ?? null,
          state: permanent.state ?? null,
          pincode: permanent.pincode ?? null,
          villageCity: permanent.villageCity ?? null,
          postOffice: permanent.postOffice ?? null,
        }
      : null,
  } as unknown as UpdateProfilePayload;
};
